/**
 * Ghost trigger + soft popover for TopBar chrome (project/team switchers).
 * Not for page forms — those keep the bordered Select.
 */

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../utils/cn';

export type ShellMenuItem = {
  id: string;
  label: string;
  selected?: boolean;
};

type Props = {
  /** Accessible name for the trigger (no visible label required). */
  ariaLabel: string;
  /** Text shown on the closed trigger. */
  triggerLabel: string;
  items: ShellMenuItem[];
  onSelect: (id: string) => void;
  /** Optional footer under the item list (e.g. Manage projects link). */
  footer?: ReactNode;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
};

/**
 * Compact chrome menu: borderless trigger, card panel, Escape / outside-click dismiss.
 */
export function ShellMenu({
  ariaLabel,
  triggerLabel,
  items,
  onSelect,
  footer,
  disabled,
  className,
  'data-testid': testId,
}: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const close = useCallback(() => setOpen(false), []);

  // Dismiss on outside pointer or Escape while open.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  return (
    <div
      ref={rootRef}
      className={cn('relative min-w-0', className)}
      data-testid={testId}
    >
      <button
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={menuId}
        className={cn(
          'inline-flex max-w-[12rem] items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-[var(--foreground)]',
          'hover:bg-[var(--muted-surface)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[var(--border)]',
          'disabled:opacity-40',
        )}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="truncate">{triggerLabel}</span>
        <ChevronDown
          size={14}
          strokeWidth={2}
          aria-hidden
          className={cn(
            'shrink-0 text-[var(--muted)] transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>

      {open ? (
        <div
          id={menuId}
          role="listbox"
          aria-label={ariaLabel}
          className="absolute left-0 top-full z-[1100] mt-1 min-w-[10rem] max-w-[16rem] overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--card)] py-1 shadow-[var(--shadow-card)]"
        >
          <ul className="max-h-64 overflow-y-auto py-0.5">
            {items.map((item) => (
              <li key={item.id} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={Boolean(item.selected)}
                  className={cn(
                    'flex w-full truncate px-3 py-1.5 text-left text-xs text-[var(--foreground)]',
                    'hover:bg-[var(--muted-surface)]',
                    item.selected && 'bg-[var(--accent-muted)]',
                  )}
                  onClick={() => {
                    onSelect(item.id);
                    close();
                  }}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
          {footer ? (
            // Footer actions (e.g. Manage link) dismiss the panel before navigation.
            <div
              className="border-t border-[var(--border)] px-3 py-1.5 text-xs"
              onClick={close}
            >
              {footer}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
